import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import RootLayout from './routes/layouts/RootLayout';
import AuthLayout from './routes/layouts/AuthLayout';
import ProtectedRoute from './routes/ProtectedRoute';
import { useAuthStore } from './stores/authStore';
import { useThemeStore } from './stores/themeStore';
import { useSocketConnection } from './hooks/useSocket';
import PWAInstallBanner from './components/pwa/PWAInstallBanner';
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';

// Auth - eager loaded (small, needed early)
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

const SocialCallbackPage = lazy(() => import('./pages/auth/SocialCallbackPage'));

// Lazy loaded pages - split by feature
const ProfilePage = lazy(() => import('./pages/auth/ProfilePage'));
const PropertySearchPage = lazy(() => import('./pages/properties/PropertySearchPage'));
const PropertyDetailPage = lazy(() => import('./pages/properties/PropertyDetailPage'));
const PropertyReportPage = lazy(() => import('./pages/properties/PropertyReportPage'));
const PropertyComparePage = lazy(() => import('./pages/properties/PropertyComparePage'));
const MarketDashboardPage = lazy(() => import('./pages/analysis/MarketDashboardPage'));
const AllTransactionsPage = lazy(() => import('./pages/analysis/AllTransactionsPage'));
const AreaAnalysisPage = lazy(() => import('./pages/analysis/AreaAnalysisPage'));
const EnginesDashboardPage = lazy(() => import('./pages/engines/EnginesDashboardPage'));
const FinancialEnginePage = lazy(() => import('./pages/engines/FinancialEnginePage'));
const MobilityEnginePage = lazy(() => import('./pages/engines/MobilityEnginePage'));
const SpatialEnginePage = lazy(() => import('./pages/engines/SpatialEnginePage'));
const InfraIndexPage = lazy(() => import('./pages/engines/InfraIndexPage'));
const ValueUpExitPage = lazy(() => import('./pages/engines/ValueUpExitPage'));
const PremiumReportPage = lazy(() => import('./pages/engines/PremiumReportPage'));
const InvestmentSimulatorPage = lazy(() => import('./pages/simulation/InvestmentSimulatorPage'));
const ScenarioBuilderPage = lazy(() => import('./pages/simulation/ScenarioBuilderPage'));
const CommunityListPage = lazy(() => import('./pages/community/CommunityListPage'));
const PostDetailPage = lazy(() => import('./pages/community/PostDetailPage'));
const WritePostPage = lazy(() => import('./pages/community/WritePostPage'));
const NeighborhoodReviewPage = lazy(() => import('./pages/community/NeighborhoodReviewPage'));
const AgentListPage = lazy(() => import('./pages/agents/AgentListPage'));
const AgentDetailPage = lazy(() => import('./pages/agents/AgentDetailPage'));
const AgentDashboardPage = lazy(() => import('./pages/agents/AgentDashboardPage'));
const AgentApplicationPage = lazy(() => import('./pages/agents/AgentApplicationPage'));
const DongtanOverviewPage = lazy(() => import('./pages/dongtan/DongtanOverviewPage'));
const DevelopmentPlanPage = lazy(() => import('./pages/dongtan/DevelopmentPlanPage'));
const InfrastructureMapPage = lazy(() => import('./pages/dongtan/InfrastructureMapPage'));
const ConsultationListPage = lazy(() => import('./pages/consultation/ConsultationListPage'));
const ConsultationPage = lazy(() => import('./pages/consultation/ConsultationPage'));
const ReportOrderPage = lazy(() => import('./pages/reports/ReportOrderPage'));
const PaidReportViewPage = lazy(() => import('./pages/reports/PaidReportViewPage'));
const MyReportsPage = lazy(() => import('./pages/reports/MyReportsPage'));
const NaverAdminPage = lazy(() => import('./pages/admin/NaverAdminPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AgentApprovalPage = lazy(() => import('./pages/admin/AgentApprovalPage'));
const IntegrationMonitorPage = lazy(() => import('./pages/admin/IntegrationMonitorPage'));
const TransactionFullPage = lazy(() => import('./pages/properties/TransactionFullPage'));
const NaverListingDetailPage = lazy(() => import('./pages/properties/NaverListingDetailPage'));
const UndervaluedPage = lazy(() => import('./pages/properties/UndervaluedPage'));
const SavedSearchesPage = lazy(() => import('./pages/properties/SavedSearchesPage'));
const SavedPropertiesPage = lazy(() => import('./pages/properties/SavedPropertiesPage'));
const NotificationPage = lazy(() => import('./pages/notifications/NotificationPage'));
const BacktestPage = lazy(() => import('./pages/analysis/BacktestPage'));
const GapAnalysisPage = lazy(() => import('./pages/analysis/GapAnalysisPage'));
const InsightsPage = lazy(() => import('./pages/insights/InsightsPage'));
const InsightDetailPage = lazy(() => import('./pages/insights/InsightDetailPage'));
const CafeInsightsPage = lazy(() => import('./pages/insights/CafeInsightsPage'));
const CafeInsightDetailPage = lazy(() => import('./pages/insights/CafeInsightDetailPage'));
const CafePostsPage = lazy(() => import('./pages/insights/CafePostsPage'));
const YoutubeVideosPage = lazy(() => import('./pages/insights/YoutubeVideosPage'));
const PolicyTimelinePage = lazy(() => import('./pages/policy/PolicyTimelinePage'));
const PolicyDetailPage = lazy(() => import('./pages/policy/PolicyDetailPage'));
const PriceAppreciationPage = lazy(() => import('./pages/analysis/PriceAppreciationPage'));
const ContagionAnalysisPage = lazy(() => import('./pages/analysis/ContagionAnalysisPage'));
const AIChatbotPage = lazy(() => import('./pages/ai/AIChatbotPage'));
const InvestmentAdvisorPage = lazy(() => import('./pages/ai/InvestmentAdvisorPage'));
const MyPriceAlertsPage = lazy(() => import('./pages/price-alerts/MyPriceAlertsPage'));
const PortfolioDashboardPage = lazy(() => import('./pages/portfolio/PortfolioDashboardPage'));
const ComplexComparePage = lazy(() => import('./pages/compare/ComplexComparePage'));
const RecommendationPage = lazy(() => import('./pages/recommendations/RecommendationPage'));
const InvestmentRankingPage = lazy(() => import('./pages/analysis/InvestmentRankingPage'));
const PersonalDashboardPage = lazy(() => import('./pages/dashboard/PersonalDashboardPage'));
const OnboardingPage = lazy(() => import('./pages/auth/OnboardingPage'));
const ChatListPage = lazy(() => import('./pages/chat/ChatListPage'));
const ChatRoomPage = lazy(() => import('./pages/chat/ChatRoomPage'));
const SiteMapPage = lazy(() => import('./pages/SiteMapPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/legal/TermsOfServicePage'));
const CalculatorToolsPage = lazy(() => import('./pages/tools/CalculatorToolsPage'));
const SchoolInfoPage = lazy(() => import('./pages/education/SchoolInfoPage'));

function PageLoader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
    </div>
  );
}

function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-8">
      <h2 className="mb-4 text-2xl font-bold text-red-600">오류가 발생했습니다</h2>
      <p className="mb-6 text-gray-600">{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        className="rounded-lg bg-primary-600 px-6 py-2 text-white hover:bg-primary-700"
      >
        다시 시도
      </button>
    </div>
  );
}

export default function App() {
  const { isAuthenticated, user, fetchProfile } = useAuthStore();
  const { initTheme } = useThemeStore();

  // 테마 초기화
  useEffect(() => {
    initTheme();
  }, [initTheme]);

  // 페이지 새로고침 시 토큰은 있지만 user 정보가 없는 경우 프로필 복원
  useEffect(() => {
    if (isAuthenticated && !user) {
      fetchProfile();
    }
  }, [isAuthenticated, user, fetchProfile]);

  // Socket.io 연결 (로그인 시 자동 연결, 로그아웃 시 해제)
  useSocketConnection();

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<PageLoader />}>
        <PWAInstallBanner />
        <Routes>
          {/* Auth pages (no header/footer) */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/auth/callback/:provider" element={<SocialCallbackPage />} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          </Route>

          {/* Main layout */}
          <Route element={<RootLayout />}>
            <Route index element={<HomePage />} />

            {/* ===== PUBLIC: 비로그인 접근 가능 ===== */}
            <Route path="/properties" element={<PropertySearchPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />

            {/* ===== 로그인 필수 (USER 이상) ===== */}
            <Route path="/naver/listings/:listingId" element={<ProtectedRoute><NaverListingDetailPage /></ProtectedRoute>} />
            <Route path="/properties/undervalued" element={<ProtectedRoute><UndervaluedPage /></ProtectedRoute>} />
            <Route path="/properties/compare" element={<ProtectedRoute><PropertyComparePage /></ProtectedRoute>} />
            <Route path="/properties/:id" element={<ProtectedRoute><PropertyDetailPage /></ProtectedRoute>} />
            <Route path="/properties/:id/transactions" element={<ProtectedRoute><TransactionFullPage /></ProtectedRoute>} />
            <Route path="/properties/:id/report" element={<ProtectedRoute><PropertyReportPage /></ProtectedRoute>} />
            <Route path="/saved-searches/filters" element={<ProtectedRoute><SavedSearchesPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><PersonalDashboardPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/sitemap" element={<ProtectedRoute><SiteMapPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationPage /></ProtectedRoute>} />

            {/* ===== PUBLIC: 도구 (비로그인 가능) ===== */}
            <Route path="/tools/calculators" element={<CalculatorToolsPage />} />

            {/* ===== USER 이상: 분석·엔진·시뮬레이션·커뮤니티·AI 등 ===== */}

            {/* Market Analysis */}
            <Route path="/analysis/market" element={<ProtectedRoute><MarketDashboardPage /></ProtectedRoute>} />
            <Route path="/analysis/transactions" element={<AllTransactionsPage />} />
            <Route path="/analysis/backtest" element={<ProtectedRoute><BacktestPage /></ProtectedRoute>} />
            <Route path="/analysis/gap" element={<ProtectedRoute><GapAnalysisPage /></ProtectedRoute>} />
            <Route path="/analysis/appreciation" element={<ProtectedRoute><PriceAppreciationPage /></ProtectedRoute>} />
            <Route path="/analysis/contagion" element={<ProtectedRoute minRole="AGENT"><ContagionAnalysisPage /></ProtectedRoute>} />
            <Route path="/analysis/ranking" element={<ProtectedRoute><InvestmentRankingPage /></ProtectedRoute>} />
            <Route path="/analysis/area/:areaCode" element={<ProtectedRoute><AreaAnalysisPage /></ProtectedRoute>} />

            {/* 5대 핵심 엔진 */}
            <Route path="/engines" element={<ProtectedRoute><EnginesDashboardPage /></ProtectedRoute>} />
            <Route path="/engines/financial" element={<ProtectedRoute><FinancialEnginePage /></ProtectedRoute>} />
            <Route path="/engines/mobility/:propertyId" element={<ProtectedRoute><MobilityEnginePage /></ProtectedRoute>} />
            <Route path="/engines/spatial/:propertyId" element={<ProtectedRoute><SpatialEnginePage /></ProtectedRoute>} />
            <Route path="/engines/infra/:propertyId" element={<ProtectedRoute><InfraIndexPage /></ProtectedRoute>} />
            <Route path="/engines/valueup/:propertyId" element={<ProtectedRoute><ValueUpExitPage /></ProtectedRoute>} />
            <Route path="/engines/report" element={<ProtectedRoute minRole="AGENT"><PremiumReportPage /></ProtectedRoute>} />

            {/* 투자 시뮬레이션 */}
            <Route path="/simulation" element={<ProtectedRoute><InvestmentSimulatorPage /></ProtectedRoute>} />
            <Route path="/simulation/scenario" element={<ProtectedRoute><ScenarioBuilderPage /></ProtectedRoute>} />

            {/* 커뮤니티 */}
            <Route path="/community" element={<ProtectedRoute><CommunityListPage /></ProtectedRoute>} />
            <Route path="/community/write" element={<ProtectedRoute><WritePostPage /></ProtectedRoute>} />
            <Route path="/community/reviews" element={<ProtectedRoute><NeighborhoodReviewPage /></ProtectedRoute>} />
            <Route path="/community/:id" element={<ProtectedRoute><PostDetailPage /></ProtectedRoute>} />

            {/* 중개사 */}
            <Route path="/agents" element={<ProtectedRoute><AgentListPage /></ProtectedRoute>} />
            <Route path="/agents/apply" element={<ProtectedRoute minRole="AGENT"><AgentApplicationPage /></ProtectedRoute>} />
            <Route path="/agents/dashboard" element={<ProtectedRoute minRole="AGENT"><AgentDashboardPage /></ProtectedRoute>} />
            <Route path="/agents/:id" element={<ProtectedRoute><AgentDetailPage /></ProtectedRoute>} />

            {/* 동탄 특화 */}
            <Route path="/dongtan" element={<ProtectedRoute><DongtanOverviewPage /></ProtectedRoute>} />
            <Route path="/dongtan/development" element={<ProtectedRoute><DevelopmentPlanPage /></ProtectedRoute>} />
            <Route path="/dongtan/infrastructure" element={<ProtectedRoute><InfrastructureMapPage /></ProtectedRoute>} />

            {/* AI 챗봇 */}
            <Route path="/ai-chat" element={<ProtectedRoute><AIChatbotPage /></ProtectedRoute>} />
            <Route path="/ai-advisor" element={<ProtectedRoute minRole="AGENT"><InvestmentAdvisorPage /></ProtectedRoute>} />

            {/* AI 상담 & 리포트 (AGENT 전용) */}
            <Route path="/consultation" element={<ProtectedRoute minRole="AGENT"><ConsultationListPage /></ProtectedRoute>} />
            <Route path="/consultation/new" element={<ProtectedRoute minRole="AGENT"><ConsultationPage /></ProtectedRoute>} />
            <Route path="/consultation/:id" element={<ProtectedRoute minRole="AGENT"><ConsultationPage /></ProtectedRoute>} />
            <Route path="/consultation/:id/order" element={<ProtectedRoute minRole="AGENT"><ReportOrderPage /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute minRole="AGENT"><MyReportsPage /></ProtectedRoute>} />
            <Route path="/reports/:id" element={<ProtectedRoute minRole="AGENT"><PaidReportViewPage /></ProtectedRoute>} />

            {/* 실시간 채팅 */}
            <Route path="/chat" element={<ProtectedRoute><ChatListPage /></ProtectedRoute>} />
            <Route path="/chat/:roomId" element={<ProtectedRoute><ChatRoomPage /></ProtectedRoute>} />

            {/* 정책 모니터 */}
            <Route path="/policies" element={<ProtectedRoute><PolicyTimelinePage /></ProtectedRoute>} />
            <Route path="/policies/:id" element={<ProtectedRoute><PolicyDetailPage /></ProtectedRoute>} />

            {/* 인사이트 */}
            <Route path="/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
            <Route path="/insights/:id" element={<ProtectedRoute><InsightDetailPage /></ProtectedRoute>} />
            <Route path="/cafe-insights" element={<ProtectedRoute><CafeInsightsPage /></ProtectedRoute>} />
            <Route path="/cafe-insights/:id" element={<ProtectedRoute><CafeInsightDetailPage /></ProtectedRoute>} />
            <Route path="/cafe-posts" element={<ProtectedRoute><CafePostsPage /></ProtectedRoute>} />
            <Route path="/youtube-videos" element={<ProtectedRoute><YoutubeVideosPage /></ProtectedRoute>} />

            {/* 관심매물 & 포트폴리오 */}
            <Route path="/saved-properties" element={<ProtectedRoute><SavedPropertiesPage /></ProtectedRoute>} />
            <Route path="/price-alerts" element={<ProtectedRoute><MyPriceAlertsPage /></ProtectedRoute>} />
            <Route path="/portfolio" element={<ProtectedRoute><PortfolioDashboardPage /></ProtectedRoute>} />
            <Route path="/compare" element={<ProtectedRoute><ComplexComparePage /></ProtectedRoute>} />
            <Route path="/recommendations" element={<ProtectedRoute><RecommendationPage /></ProtectedRoute>} />

            {/* 학군 정보 */}
            <Route path="/education/schools" element={<ProtectedRoute><SchoolInfoPage /></ProtectedRoute>} />

            {/* ===== ADMIN 전용 ===== */}
            <Route path="/admin/dashboard" element={<ProtectedRoute minRole="ADMIN"><AdminDashboardPage /></ProtectedRoute>} />
            <Route path="/admin/naver" element={<ProtectedRoute minRole="ADMIN"><NaverAdminPage /></ProtectedRoute>} />
            <Route path="/admin/integrations" element={<ProtectedRoute minRole="ADMIN"><IntegrationMonitorPage /></ProtectedRoute>} />
            <Route path="/admin/agent-approvals" element={<ProtectedRoute minRole="ADMIN"><AgentApprovalPage /></ProtectedRoute>} />

            {/* Catch-all */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
